import { useState } from 'react';
import ConsentCheckbox from './ConsentCheckbox';

// v1.4 — NEW-163 Increment B (2026-10-30): loved-one capture inside the share step (plan v0.5 §4.5,
//   spec v0.2 §5). When the member picks "Everyone in the group", an inline LovedOneNameField
//   appears under that radio: "Who are you remembering?" (copy v1.0 B-9), prefilled from
//   space.lovedOneName when a name is on file (helper B-11) or empty with the transparency copy
//   (B-10). Sharing with the group with no name is held with B-12 (an offer, not a wall); the
//   other two audiences never show the field and nothing blocks. onShare(vp, opts) gains
//   opts.lovedOneName ONLY when scope === 'group' and the name differs from the one on file
//   (the page PATCHes it before the create). Gated on space.isGroupSpace: in a private/memorial
//   space, or when scope ≠ group, the picker is byte-identical to v1.3; legacy one-argument
//   onShare calls are unchanged.
//   Note (decision 4): this field is the edit surface; removing a name outright is not reachable
//   here because the group share requires one — a remove affordance is the deferred panel item.
// v1.3 — Consent copy (Oct 4, 2026; copy v0.4 §3.1): KC-1 restored to "...I share with them" (v0.4 SETTLED; supersedes the in-session drop of "with them"). Save-picker stays the explicit wording.
// v1.2 — Consent PR E3a (Oct 4, 2026; spec PR-E v0.2 §2 E3; plan v0.26 §8 D9/D9a; copy v0.3 §3.1):
//   inside the scope picker, under the two scope radios, the shared ConsentCheckbox for the
//   contributor_keep_copy notice (KC-1 "Let {owner} keep a copy of the memories I share with them",
//   helper KC-2). Shown only when space.keepCopyCaptureEnabled && !space.keepCopyAllowed, and only
//   when the server has the notice text (the component hides itself on 404 — fail closed).
//   onShare(scope, { keepCopy, keepCopyShownHash }) — the second argument only when ticked. New
//   optional props space + api; without them the picker is byte-identical to v1.1.

// v1.1 — PR-6 / NEW-64: "Save just for me" shown to every contributor and
//   fires onSavePrivate (private contributor_only create, no own-space copy);
//   dropped ownSpaceId + onSaveJustForMe props. (2026-09-04)
// B1b U1 — contributor two-button save + select-then-confirm scope picker.
//
// Rendered by the contributor capture pages ONLY when group spaces is ON
// (the flag gate lives in each page: space?.groupSpacesEnabled === true).
// - "Save just for me" saves the memory PRIVATE in this space (contributor_only)
//   and is shown to every contributor (PR-6 / NEW-64 — no own-space copy).
// - "Share this memory" opens a select-then-confirm scope picker.
//
// Copy is verbatim from Anamoria_Contributor_Sharing_Options_Copy_v0.7
// (SV-1/SV-2, SH-2/SH-3, SH-3a). Group wording only (O-2 = group for the pilot).
//
// Self-contained inline styles on purpose: no CSS-module edits, so the shared
// member capture modules stay untouched (BL-1-safe).
export default function ContributorSaveActions({ saving, onSavePrivate, onShare, space = null, api = null }) {
  const [picking, setPicking] = useState(false);
  const [scope, setScope] = useState('owner'); // 'owner' | 'group'
  const [keepCopy, setKeepCopy] = useState(false);          // E3a: unticked by default (ICO)
  const [keepCopyHash, setKeepCopyHash] = useState(null);
  const offerKeepCopy = Boolean(api && space?.keepCopyCaptureEnabled === true && space?.keepCopyAllowed !== true);
  const ownerName = space?.ownerName || 'the space owner';
  // v1.4 (NEW-163): loved-one name — GROUP spaces only (private/memorial spaces never show the field —
  // plan v0.5 §10 regression list); initial value from the contributor space GET.
  const offerLovedOne = space?.isGroupSpace === true;
  const nameOnFile = (typeof space?.lovedOneName === 'string' ? space.lovedOneName : '').trim();
  const [lovedOne, setLovedOne] = useState(nameOnFile);
  const [lovedOneMissing, setLovedOneMissing] = useState(false);
  const leaderFirst = (ownerName || '').trim().split(/\s+/)[0] || 'the space owner';
  const shareArgs = () => {
    const kc = offerKeepCopy && keepCopy && keepCopyHash ? { keepCopy: true, keepCopyShownHash: keepCopyHash } : null;
    const lo = offerLovedOne && scope === 'group' && lovedOne.trim() !== nameOnFile ? { lovedOneName: lovedOne.trim() } : null;
    if (!kc && !lo) return undefined;
    return { ...(kc || {}), ...(lo || {}) };
  };

  if (picking) {
    return (
      <div style={S.wrap}>
        <h3 style={S.heading}>Who can see this memory?</h3>

        <label style={S.option}>
          <input
            type="radio"
            name="contributor-scope"
            checked={scope === 'owner'}
            onChange={() => setScope('owner')}
          />
          <span style={S.optText}>
            <span style={S.optTitle}>The space owner</span>
            <span style={S.optGloss}>You and the space owner can see this.</span>
          </span>
        </label>

        <label style={S.option}>
          <input
            type="radio"
            name="contributor-scope"
            checked={scope === 'group'}
            onChange={() => setScope('group')}
          />
          <span style={S.optText}>
            <span style={S.optTitle}>Everyone in the group</span>
            <span style={S.optGloss}>
              The space owner sees this first, and can then share it with everyone in the group.
            </span>
          </span>
        </label>

        {offerLovedOne && scope === 'group' && (
          <LovedOneNameField
            value={lovedOne}
            nameOnFile={nameOnFile}
            leaderFirst={leaderFirst}
            missing={lovedOneMissing}
            disabled={saving}
            onChange={(v) => { setLovedOne(v); if (lovedOneMissing && v.trim()) setLovedOneMissing(false); }}
          />
        )}

        {offerKeepCopy && (
          <div style={S.keepCopy}>
            <ConsentCheckbox
              api={api}
              docType="contributor_keep_copy"
              ownerName={ownerName}
              label={`Let ${ownerName} keep a copy of the memories I share with them`}
              checked={keepCopy}
              onChange={(checked, hash) => { setKeepCopy(checked); setKeepCopyHash(hash); }}
              disabled={saving}
            />
            <p style={S.keepCopyHelper}>They could save a copy to keep, along with their own memories. You can change this anytime.</p>
          </div>
        )}

        <button
          type="button"
          style={S.primary}
          disabled={saving}
          onClick={() => {
            const vp = scope === 'group' ? 'group' : 'owner_contributor_only';
            if (offerLovedOne && scope === 'group' && !lovedOne.trim()) { setLovedOneMissing(true); return; }   // v1.4 (NEW-163): B-12, an offer not a wall
            const kc = shareArgs();
            if (kc) onShare(vp, kc); else onShare(vp);   // one argument when nothing extra: legacy callers/tests unchanged
          }}
        >
          {saving ? 'Saving…' : 'Share this memory'}
        </button>
        <button
          type="button"
          style={S.ghost}
          disabled={saving}
          onClick={() => setPicking(false)}
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <button
        type="button"
        style={S.primary}
        disabled={saving}
        onClick={onSavePrivate}
      >
        Save just for me
      </button>
      <button
        type="button"
        style={S.primary}
        disabled={saving}
        onClick={() => setPicking(true)}
      >
        Share this memory
      </button>
    </div>
  );
}

/* v1.4 (NEW-163): inline loved-one field for the group share (copy v1.0 B-9 … B-12). Rebuilt inline
   here on purpose — not the standalone feed block that was reverted (item-3 log §2). */
export function LovedOneNameField({ value, nameOnFile, leaderFirst, missing, disabled, onChange }) {
  const shown = value.trim() || '…';
  return (
    <div style={{ ...S.lovedOne, ...(missing ? S.lovedOneMissing : {}) }} data-testid="loved-one-field">
      <label htmlFor="contributor-loved-one" style={S.lovedOneLabel}>Who are you remembering?</label>
      <input
        id="contributor-loved-one"
        type="text"
        value={value}
        maxLength={255}
        autoComplete="off"
        disabled={disabled}
        aria-invalid={missing ? 'true' : undefined}
        aria-describedby={missing ? 'contributor-loved-one-needed' : 'contributor-loved-one-helper'}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...S.lovedOneInput, ...(missing ? S.lovedOneInputMissing : {}) }}
      />
      <p id="contributor-loved-one-helper" style={S.lovedOneHelper}>
        {nameOnFile
          ? `Shown as "remembering ${shown}" on what you share with the group. You can change or remove it.`
          : `This will show as "remembering ${shown}" on what you share with the group. You can change or remove it.`}
      </p>
      {missing && (
        <p id="contributor-loved-one-needed" role="alert" style={S.lovedOneNeeded}>
          To share with the group, add who you're remembering. You can also keep it just for yourself, or just for {leaderFirst}.
        </p>
      )}
    </div>
  );
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 12, width: '100%' },
  // v1.4 — Sophie, live render 2026-10-30: the field was easy to miss and ran into the keep-a-copy
  // checkbox. Now its own tinted, bordered box, attached to the group radio (terracotta left rule,
  // the same treatment as the quoted note), with clear space before whatever follows.
  lovedOne: {
    display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px',
    marginTop: -4, marginBottom: 6,
    background: '#F6F0EA', border: '1px solid rgba(160, 128, 92, .45)', borderLeft: '4px solid var(--moment-accent, #744D2E)',
    borderRadius: 12,
  },
  lovedOneMissing: { borderColor: 'var(--moment-accent, #744D2E)' },
  lovedOneLabel: { fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary, #2B2A2A)' },
  lovedOneInput: {
    width: '100%', boxSizing: 'border-box', padding: '11px 12px', fontSize: 16,
    border: '1px solid #cfc9c0', borderRadius: 10, background: 'var(--color-bg-white, #fff)',
    fontFamily: 'inherit',
  },
  lovedOneInputMissing: { border: '2px solid var(--moment-accent, #744D2E)' },
  lovedOneHelper: { fontSize: 13, opacity: 0.85, margin: 0 },
  lovedOneNeeded: { fontSize: 14, fontWeight: 700, margin: 0, color: 'var(--moment-accent, #744D2E)' },
  heading: { margin: '0 0 4px', fontSize: 16, fontWeight: 600 },
  option: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    padding: 12,
    border: '1px solid #d9d4cc',
    borderRadius: 12,
    cursor: 'pointer',
  },
  optText: { display: 'flex', flexDirection: 'column', gap: 2 },
  optTitle: { fontWeight: 600 },
  optGloss: { fontSize: 13, opacity: 0.8 },
  reassure: { fontSize: 13, marginTop: 6, opacity: 0.9 },
  keepCopy: { padding: '4px 2px 0', borderTop: '1px solid #e8e3dc', marginTop: 2, paddingTop: 12 },
  keepCopyHelper: { fontSize: 13, opacity: 0.8, margin: '0 0 4px 30px' },
  primary: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 999,
    border: 'none',
    background: 'var(--color-sage-dark, #5b7a65)',
    color: 'var(--color-text-white, #fff)',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
  ghost: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 999,
    border: '1px solid #cfc9c0',
    background: 'transparent',
    fontSize: 15,
    cursor: 'pointer',
  },
};
